import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { LunchMenu, LunchMenuSummary } from "../../../../api/lunchMenus";
import { useLunchMenus } from "../../../../hooks/useLunchMenus";
import { useViewport } from "../../../../hooks/useViewport";
import OrderingTopBar from "../../components/OrderingTopBar";
import { ISO_DATE } from "../lunchDraft";
import { LunchMenuCard } from "./LunchMenuCard";
import { LunchMenuDetail } from "./LunchMenuDetail";
import { LunchMenusEmpty } from "./LunchMenusEmpty";
import { LunchMenusSidebar } from "./LunchMenusSidebar";
import { LunchMenusTopBar } from "./LunchMenusTopBar";

interface LunchMenuListProps {
  selectedMenuId: number | null;
  onSelect: (menuId: number | null) => void;
  onNew: () => void;
  onEdit: (menu: LunchMenu) => void;
  onCopy: (menu: LunchMenu) => void;
}

/** Групите в списъка: днес (активно сега или по-късно днес) · предстоящи · минали. */
const groupMenus = (menus: LunchMenuSummary[]) => {
  const today = dayjs().format(ISO_DATE);
  const isToday = (menu: LunchMenuSummary) =>
    menu.status === "live" ||
    (menu.status === "upcoming" && menu.nextDate === today);
  return [
    {
      key: "today",
      titleKey: "lunchMenu.kickerToday",
      menus: menus.filter(isToday),
    },
    {
      key: "upcoming",
      titleKey: "lunchMenu.kickerUpcoming",
      menus: menus
        .filter((menu) => menu.status === "upcoming" && !isToday(menu))
        .sort((a, b) => (a.nextDate ?? "").localeCompare(b.nextDate ?? "")),
    },
    {
      key: "past",
      titleKey: "lunchMenu.kickerPast",
      menus: menus.filter((menu) => menu.status === "past"),
    },
  ].filter((group) => group.menus.length > 0);
};

/**
 * Екранът със списъка на обедните менюта. Десктоп: списък вляво + детайл
 * вдясно + футър (Назад · Ново). Под десктоп: списъкът, а избраното меню
 * отваря детайла на цял екран (стрелката в горната лента връща към списъка).
 */
export const LunchMenuList = ({
  selectedMenuId,
  onSelect,
  onNew,
  onEdit,
  onCopy,
}: LunchMenuListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDesktop } = useViewport();
  const { data: menus = [], isLoading } = useLunchMenus();
  const groups = useMemo(() => groupMenus(menus), [menus]);
  const isEmpty = !isLoading && menus.length === 0;

  // На десктоп винаги има избрано меню (първото), ако изобщо има менюта
  const firstMenuId = groups[0]?.menus[0]?.id ?? null;
  const selectedExists = menus.some((menu) => menu.id === selectedMenuId);
  useEffect(() => {
    if (isDesktop && !selectedExists && firstMenuId != null)
      onSelect(firstMenuId);
  }, [isDesktop, selectedExists, firstMenuId, onSelect]);

  const detail =
    selectedMenuId != null && selectedExists ? (
      <LunchMenuDetail
        key={selectedMenuId}
        menuId={selectedMenuId}
        onEdit={onEdit}
        onCopy={onCopy}
        onRemoved={() => onSelect(null)}
      />
    ) : null;

  const list = isLoading ? (
    <div className="grid place-items-center py-16">
      <Spin />
    </div>
  ) : (
    groups.map((group) => (
      <div key={group.key} className="flex flex-col gap-2">
        <span className="mt-2 font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-pos-sec-head">
          {t(group.titleKey)}
        </span>
        {group.menus.map((menu) => (
          <LunchMenuCard
            key={menu.id}
            menu={menu}
            selected={menu.id === selectedMenuId}
            onSelect={onSelect}
          />
        ))}
      </div>
    ))
  );

  const newButton = (
    <button
      type="button"
      onClick={onNew}
      className="relative inline-flex h-[52px] cursor-pointer items-center justify-center gap-2 rounded-[13px] border-0 bg-pos-accent px-7 text-[15px] font-bold text-white transition-colors hover:bg-pos-accent-ink"
    >
      <PlusOutlined />
      {t("lunchMenu.newMenu")}
    </button>
  );

  if (!isDesktop)
    return (
      <>
        <OrderingTopBar
          onBack={
            selectedMenuId != null
              ? () => onSelect(null)
              : () => navigate("/ordering")
          }
        />
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3.5 pb-[calc(84px+env(safe-area-inset-bottom,0px)*0.5)] pt-3">
          {isEmpty ? (
            <LunchMenusEmpty onNew={onNew} />
          ) : (
            (detail ?? (
              <>
                <h1 className="m-0 text-center text-lg font-extrabold leading-[1.2] text-pos-ink">
                  {t("lunchMenu.title")}
                </h1>
                {list}
              </>
            ))
          )}
        </div>
        {!detail && !isEmpty && (
          <div className="fixed inset-x-0 bottom-0 z-50 flex border-t border-pos-line bg-pos-panel px-3.5 pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)] pt-2.5 [&>button]:w-full">
            {newButton}
          </div>
        )}
      </>
    );

  return (
    <div className="flex min-h-0 flex-1">
      <LunchMenusSidebar onNew={isEmpty ? undefined : onNew}>
        {isEmpty ? null : list}
      </LunchMenusSidebar>
      <div className="flex min-w-0 flex-1 flex-col">
        <LunchMenusTopBar />
        {isEmpty ? (
          <LunchMenusEmpty onNew={onNew} />
        ) : (
          <section className="min-h-0 flex-1 overflow-y-auto px-7 py-6">
            {detail ??
              (!isLoading && (
                <p className="m-0 py-10 text-center text-pos-soft">
                  {t("lunchMenu.pickMenu")}
                </p>
              ))}
          </section>
        )}
      </div>
    </div>
  );
};
